import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import {
  LayoutGrid, Map as MapIcon, QrCode, Lock, RotateCw, RefreshCw, MapPin, MessageSquare,
  Download, Loader2, Folder, CheckSquare, Square, X, Shield, ShieldOff, Monitor, Package, ArrowUpCircle,
  FolderTree, ShieldCheck, Zap,
} from 'lucide-react'
import { useMdmFleet, useSendMdmBulkCommand } from '../../../hooks/useMdm'
import DeviceStatusBadge from '../../../components/DeviceStatusBadge'
import EnrollQrModal from './EnrollQrModal'
import ManagedAppsModal from './ManagedAppsModal'
import FieldsModal from './FieldsModal'
import AgentVersionModal from './AgentVersionModal'
import ScopePolicyModal from './ScopePolicyModal'
import ScopeActionsModal from './ScopeActionsModal'
import MdmMap from './MdmMap'
import ScreenshotThumb from './ScreenshotThumb'
import { BatteryIndicator, NetworkIndicator } from './mobileUi'
import { timeAgo, errorMessage, localStateBadges, MESSAGE_FIELDS, messagePayload } from './mobileUtils'

const VIEW_KEY = 'mdm.fleet.view'

function readView() {
  try { return localStorage.getItem(VIEW_KEY) === 'map' ? 'map' : 'cards' } catch { return 'cards' }
}

function isLowBattery(d) {
  return d.batteryLevel != null && d.batteryLevel < 20 && !d.charging
}

function statusColor(d) {
  const s = String(d.status || '').toLowerCase()
  if (s === 'online') return isLowBattery(d) ? '#f59e0b' : '#10b981'
  if (s === 'error') return '#ef4444'
  return '#9ca3af'
}

/**
 * Aba "Mobile" da tela de Managed Devices: frota de tablets/celulares do MDM.
 *
 * `devices` vem do pai já filtrado por aba/busca/status/grupo (ManagedDeviceDto);
 * os dados MDM (bateria, rede, posição...) vêm de /mdm/fleet e são mesclados por id.
 */
export default function MobileFleet({
  devices = [],
  groups = [],
  isLoading = false,
  currentGroupId = null,
  currentGroupName = null,
  scopeDeviceCount = 0,
  mobileGroupIds = null,
  showAll = false,
  onOpenGroup,
}) {
  const navigate = useNavigate()
  const { data: fleet = [], isLoading: fleetLoading } = useMdmFleet()
  const bulk = useSendMdmBulkCommand()

  const [view, setView] = useState(readView)
  const [selected, setSelected] = useState(() => new Set())
  const [enrollOpen, setEnrollOpen] = useState(false)
  const [appsOpen, setAppsOpen] = useState(false)
  const [modal, setModal] = useState(null) // 'message' | 'install' | 'agent-version' | 'scope-policy' | 'scope-actions'
  const [feedback, setFeedback] = useState(null) // { type: 'ok'|'error', text }

  const changeView = (v) => {
    setView(v)
    try { localStorage.setItem(VIEW_KEY, v) } catch { /* storage indisponível */ }
  }

  const items = useMemo(() => {
    const byId = new Map(fleet.map((f) => [String(f.id), f]))
    return devices.map((d) => ({ ...d, ...(byId.get(String(d.id)) || {}), name: d.name }))
  }, [devices, fleet])

  const kpis = useMemo(() => {
    const online = items.filter((d) => String(d.status).toLowerCase() === 'online').length
    return [
      { label: 'Total', value: items.length },
      { label: 'Online', value: online, className: 'text-green-700' },
      { label: 'Offline', value: items.length - online, className: 'text-gray-600' },
      { label: 'Bateria baixa', value: items.filter(isLowBattery).length, className: 'text-amber-700' },
      { label: 'Em quiosque', value: items.filter((d) => d.kioskActive).length, className: 'text-indigo-700' },
      { label: 'Sem VPN', value: items.filter((d) => d.vpnConnected === false && d.vpnEnabled !== false).length, className: 'text-red-700' },
    ]
  }, [items])

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const allSelected = items.length > 0 && items.every((d) => selected.has(d.id))
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(items.map((d) => d.id)))
  const selectedIds = items.filter((d) => selected.has(d.id)).map((d) => d.id)

  const runBulk = async (type, payload = null, label = type) => {
    if (!selectedIds.length) return
    setFeedback(null)
    try {
      await bulk.mutateAsync({ deviceIds: selectedIds, type, payload })
      setFeedback({ type: 'ok', text: `"${label}" enviado para ${selectedIds.length} dispositivo(s).` })
      setModal(null)
    } catch (e) {
      setFeedback({ type: 'error', text: errorMessage(e, 'Falha ao enviar o comando em lote.') })
    }
  }

  const markers = items.map((d) => ({
    id: d.id,
    lat: d.lat,
    lng: d.lng,
    color: statusColor(d),
    label: `${d.name}${d.batteryLevel != null ? ` — ${d.batteryLevel}%` : ''}${d.locationAt ? ` — ${timeAgo(d.locationAt)}` : ''}`,
    onClick: () => navigate(`/managed-devices/${d.id}`),
  }))
  const withoutPosition = items.filter((d) => d.lat == null || d.lng == null).length

  const loading = isLoading || (fleetLoading && !fleet.length)

  return (
    <div className="space-y-4 p-4">
      {/* KPIs */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
        {kpis.map((k) => (
          <div key={k.label} className="border border-gray-200 rounded-lg px-3 py-2 bg-white">
            <div className="text-xs uppercase tracking-wide text-gray-400">{k.label}</div>
            <div className={clsx('text-2xl font-semibold', k.className || 'text-gray-900')}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Barra de ações */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
          <button
            type="button"
            onClick={() => changeView('cards')}
            className={clsx('flex items-center gap-1 px-3 py-1.5 text-sm', view === 'cards' ? 'bg-theme-primary text-white' : 'bg-white text-gray-600 hover:bg-gray-50')}
          >
            <LayoutGrid className="w-4 h-4" /> Cards
          </button>
          <button
            type="button"
            onClick={() => changeView('map')}
            className={clsx('flex items-center gap-1 px-3 py-1.5 text-sm', view === 'map' ? 'bg-theme-primary text-white' : 'bg-white text-gray-600 hover:bg-gray-50')}
          >
            <MapIcon className="w-4 h-4" /> Mapa
          </button>
        </div>

        {view === 'cards' && items.length > 0 && (
          <button type="button" onClick={toggleAll} className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900">
            {allSelected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
            Selecionar todos
          </button>
        )}

        <div className="flex-1" />

        <button
          type="button"
          onClick={() => setAppsOpen(true)}
          className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 bg-white text-gray-700 rounded-lg hover:bg-gray-50"
        >
          <Package className="w-4 h-4" /> Apps gerenciados
        </button>

        <button
          type="button"
          onClick={() => setEnrollOpen(true)}
          className="flex items-center gap-2 px-4 py-2 text-sm bg-theme-primary text-white rounded-lg hover:bg-theme-primary/90"
        >
          <QrCode className="w-4 h-4" /> Cadastrar tablet
        </button>
      </div>

      {/* Escopo atual: o que vale para o grupo vale para os subgrupos (resolvido no servidor) */}
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm">
        <FolderTree className="w-4 h-4 text-gray-500" />
        <span className="text-gray-700">
          {currentGroupId ? <>Grupo <strong>{currentGroupName || '—'}</strong></> : <strong>Todos os tablets</strong>}
          <span className="text-gray-500"> · {scopeDeviceCount} tablet(s){currentGroupId ? ' com subgrupos' : ''}</span>
        </span>
        <div className="flex-1" />
        <BulkButton icon={Zap} label="Ação no grupo" disabled={scopeDeviceCount === 0} onClick={() => setModal('scope-actions')} />
        <BulkButton icon={ShieldCheck} label="Política do grupo" onClick={() => setModal('scope-policy')} />
        <BulkButton icon={ArrowUpCircle} label="Versão do MDM" onClick={() => setModal('agent-version')} />
      </div>

      {/* Ações em lote */}
      {selectedIds.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 px-3 py-2 bg-theme-primary/5 border border-theme-primary/30 rounded-lg text-sm">
          <span className="font-medium text-gray-800">{selectedIds.length} selecionado(s):</span>
          <BulkButton icon={Lock} label="Bloquear" disabled={bulk.isPending} onClick={() => runBulk('lock', null, 'Bloquear')} />
          <BulkButton icon={RotateCw} label="Reiniciar" disabled={bulk.isPending} onClick={() => runBulk('reboot', null, 'Reiniciar')} />
          <BulkButton icon={MapPin} label="Localizar" disabled={bulk.isPending} onClick={() => runBulk('locate', null, 'Localizar')} />
          <BulkButton icon={RefreshCw} label="Aplicar política" disabled={bulk.isPending} onClick={() => runBulk('sync-policy', null, 'Sincronizar política')} />
          <BulkButton icon={MessageSquare} label="Mensagem" disabled={bulk.isPending} onClick={() => setModal('message')} />
          <BulkButton icon={Download} label="Instalar app (URL)" disabled={bulk.isPending} onClick={() => setModal('install')} />
          {bulk.isPending && <Loader2 className="w-4 h-4 animate-spin text-gray-500" />}
          <button type="button" onClick={() => setSelected(new Set())} className="ml-auto p-1 text-gray-500 hover:text-gray-800" title="Limpar seleção">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {feedback && (
        <div className={clsx('px-3 py-2 rounded-lg text-sm border', feedback.type === 'ok' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-700')}>
          {feedback.text}
        </div>
      )}

      {/* Grupos do nível atual */}
      {!showAll && groups.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {groups.map((g) => (
            <button
              key={g.id}
              type="button"
              onClick={() => onOpenGroup?.(g)}
              className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white hover:bg-gray-50"
            >
              <Folder className="w-4 h-4 text-amber-500" />
              {g.name}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Carregando frota...
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p>Nenhum tablet {currentGroupId ? 'neste grupo' : 'cadastrado'}.</p>
          <button type="button" onClick={() => setEnrollOpen(true)} className="mt-3 text-sm text-theme-primary hover:underline">
            Cadastrar o primeiro tablet via QR
          </button>
        </div>
      ) : view === 'map' ? (
        <div className="space-y-2">
          <MdmMap markers={markers} height={520} />
          {withoutPosition > 0 && (
            <p className="text-xs text-gray-500">{withoutPosition} dispositivo(s) sem posição conhecida não aparecem no mapa.</p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {items.map((d) => (
            <FleetCard
              key={d.id}
              device={d}
              selected={selected.has(d.id)}
              onToggle={() => toggle(d.id)}
              onOpen={() => navigate(`/managed-devices/${d.id}`)}
            />
          ))}
        </div>
      )}

      <EnrollQrModal isOpen={enrollOpen} onClose={() => setEnrollOpen(false)} currentGroupId={currentGroupId} allowedGroupIds={mobileGroupIds} />

      <ScopePolicyModal
        isOpen={modal === 'scope-policy'}
        onClose={() => setModal(null)}
        groupId={currentGroupId}
        groupName={currentGroupName}
        onFeedback={setFeedback}
      />

      <ScopeActionsModal
        isOpen={modal === 'scope-actions'}
        onClose={() => setModal(null)}
        groupId={currentGroupId}
        groupName={currentGroupName}
        deviceCount={scopeDeviceCount}
        onFeedback={setFeedback}
      />

      <ManagedAppsModal
        isOpen={appsOpen}
        onClose={() => setAppsOpen(false)}
        devices={items}
        preselectedIds={selectedIds}
        scopeGroupId={currentGroupId}
        scopeName={currentGroupId ? `grupo ${currentGroupName || ''}`.trim() : 'todos os tablets'}
        scopeCount={scopeDeviceCount}
        onFeedback={setFeedback}
      />

      <AgentVersionModal
        isOpen={modal === 'agent-version'}
        onClose={() => setModal(null)}
        groupId={currentGroupId}
        groupName={currentGroupName}
        onFeedback={setFeedback}
      />

      <FieldsModal
        isOpen={modal === 'message'}
        onClose={() => setModal(null)}
        title={`Mensagem para ${selectedIds.length} dispositivo(s)`}
        fields={MESSAGE_FIELDS}
        loading={bulk.isPending}
        onSubmit={(v) => runBulk('message', messagePayload(v), 'Mensagem')}
      />

      <FieldsModal
        isOpen={modal === 'install'}
        onClose={() => setModal(null)}
        title={`Instalar app em ${selectedIds.length} dispositivo(s)`}
        description="O APK é baixado pelo próprio tablet a partir da URL (HTTPS)."
        fields={[
          { key: 'url', label: 'URL do APK', placeholder: 'https://…/app.apk', required: true },
          { key: 'sha256', label: 'SHA-256 (hex, opcional)', placeholder: 'confere a integridade do download' },
        ]}
        loading={bulk.isPending}
        onSubmit={(v) => runBulk('install-apk', { url: v.url.trim(), sha256: v.sha256?.trim() || null }, 'Instalar APK')}
      />
    </div>
  )
}

function BulkButton({ icon: Icon, label, onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex items-center gap-1 px-2.5 py-1 border border-gray-300 bg-white rounded-md hover:bg-gray-50 disabled:opacity-50"
    >
      <Icon className="w-4 h-4" /> {label}
    </button>
  )
}

function AgentVersionLine({ device: d }) {
  if (!d.agentVersion) return null
  const state = d.agentUpdateState
  return (
    <div className="flex items-center gap-1 text-xs text-gray-500" title={d.agentUpdateError || undefined}>
      <span>MDM {d.agentVersion}</span>
      {state === 'updating' && <span className="text-amber-700">→ {d.agentTargetVersion}</span>}
      {state === 'failing' && <span className="text-red-600">→ {d.agentTargetVersion} (falha ao atualizar)</span>}
    </div>
  )
}

/** Selos de situação local que pedem atenção na frota (agente ≥ 1.1). */
const FLEET_BADGES = ['removed', 'noOwner', 'crashloop', 'paused', 'suspended']

function FleetCard({ device: d, selected, onToggle, onOpen }) {
  const badges = localStateBadges(d).filter((b) => FLEET_BADGES.includes(b.key))
  return (
    <div
      className={clsx(
        'relative bg-white border rounded-lg overflow-hidden cursor-pointer transition-shadow hover:shadow-md',
        selected ? 'border-theme-primary ring-1 ring-theme-primary' : 'border-gray-200'
      )}
      onClick={onOpen}
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onToggle() }}
        className="absolute top-2 left-2 z-10 bg-white/90 rounded p-0.5 text-gray-600 hover:text-gray-900"
        title="Selecionar"
      >
        {selected ? <CheckSquare className="w-5 h-5 text-theme-primary" /> : <Square className="w-5 h-5" />}
      </button>
      {d.kioskActive && (
        <span className="absolute top-2 right-2 z-10 flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-indigo-600 text-white">
          <Monitor className="w-3 h-3" /> Quiosque
        </span>
      )}

      <ScreenshotThumb deviceId={d.id} fileId={d.lastScreenshotFileId} className="w-full h-36" />

      <div className="p-3 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="font-medium text-gray-900 truncate" title={d.name}>{d.name}</div>
            <div className="text-xs text-gray-500 truncate">
              {[d.manufacturer, d.model].filter(Boolean).join(' ') || d.kindName || d.kind}
              {d.androidVersion ? ` · Android ${d.androidVersion}` : ''}
            </div>
          </div>
          <DeviceStatusBadge status={d.status} />
        </div>
        <div className="flex items-center justify-between text-sm">
          <BatteryIndicator level={d.batteryLevel} charging={d.charging} />
          <NetworkIndicator type={d.networkType} signalLevel={d.signalLevel} />
        </div>
        {badges.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {badges.map((b) => (
              <span key={b.key} className={clsx('px-1.5 py-0.5 text-xs rounded', b.className)} title={b.label}>{b.short}</span>
            ))}
          </div>
        )}
        <AgentVersionLine device={d} />
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span title={d.lastSeenAt || ''}>Visto {timeAgo(d.lastSeenAt)}</span>
          <span className="flex items-center gap-2">
            {d.vpnEnabled === false
              ? null
              : d.vpnConnected === false
                ? <span className="flex items-center gap-0.5 text-red-600"><ShieldOff className="w-3.5 h-3.5" />VPN</span>
                : d.vpnConnected
                  ? <span className="flex items-center gap-0.5 text-green-700"><Shield className="w-3.5 h-3.5" />VPN</span>
                  : null}
            {d.lat != null && <MapPin className="w-3.5 h-3.5" title="Posição conhecida" />}
          </span>
        </div>
      </div>
    </div>
  )
}
